import type {CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {RefObject} from 'react'
import {useCallback, useMemo, useState} from 'react'

import type {CopilotGenerateDescriptionPanelProps} from './CopilotGenerateDescriptionPanel'

export type UseCopilotGeneratePanelOptions = {
  /** Reference to the editor component, used for focus management and panel positioning */
  editorRef: RefObject<CommentBoxHandle | null>
  /** Current body/description content */
  body: string
  /** Callback to update the body when content is generated */
  onChange: (newBody: string, newTitle?: string) => void
  /** Whether Copilot functionality should be shown (e.g., based on user permissions) */
  shouldShowCopilotFunctionality?: boolean
  /** Repository owner for context */
  repoOwner?: string
  /** Repository name for context */
  repoName?: string
  /** Current title for context (optional, not updated by hook) */
  title?: string
  /** Whether the panel should generate titles. Defaults to false for body-only editing. */
  generateTitle?: boolean
  /** Command ID for the keyboard shortcut to toggle the panel */
  commandId?: string
  /** Additional condition that must be true to allow toggling (e.g., viewMode !== 'preview') */
  canToggle?: boolean
}

export type UseCopilotGeneratePanelResult = {
  /** Whether the Copilot panel is currently open */
  isPanelOpen: boolean
  /** Whether content is currently being generated */
  isGenerating: boolean
  /** Current height of the panel in pixels */
  panelHeight: number
  /** Whether all conditions are met to show Copilot functionality */
  shouldShowCopilot: boolean
  /** Toggle the panel open/closed */
  togglePanel: () => void
  /** Close the panel and return focus to editor */
  closePanel: () => void
  /** Handler for when content is generated */
  handleGenerate: (title: string, description: string) => void
  /** Handler for editor input focus (closes panel when anchored to top with empty body) */
  handleInputFocus: () => void
  /** Commands object for use with ScopedCommands */
  commands: Record<string, () => void>
  /** Props to spread on CopilotGenerateDescriptionPanel component */
  panelProps: Omit<CopilotGenerateDescriptionPanelProps, 'className'>
  /** Container style with CSS variable for bottom padding when panel is open */
  containerStyle: React.CSSProperties | undefined
  /** Handler for pending state changes from the panel */
  setIsGenerating: (isPending: boolean) => void
  /** Handler for panel height changes */
  setPanelHeight: (height: number) => void
}

const DEFAULT_COMMAND_ID = 'copilot-panel:toggle'

/**
 * Hook to manage the state and behavior of the CopilotGenerateDescriptionPanel.
 * Encapsulates panel open/close state, generating state, height tracking,
 * keyboard commands, and computed props for the panel component.
 */
export function useCopilotGeneratePanel({
  editorRef,
  body,
  onChange,
  shouldShowCopilotFunctionality = false,
  repoOwner = '',
  repoName = '',
  title = '',
  generateTitle = false,
  commandId = DEFAULT_COMMAND_ID,
  canToggle = true,
}: UseCopilotGeneratePanelOptions): UseCopilotGeneratePanelResult {
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [panelHeight, setPanelHeight] = useState(0)

  const isWriteIssueBodyWithCopilotEnabled = isFeatureEnabled('write_issue_body_with_copilot')
  const shouldShowCopilot =
    isWriteIssueBodyWithCopilotEnabled && shouldShowCopilotFunctionality && Boolean(repoOwner) && Boolean(repoName)

  // Close the panel and return focus to the editor
  const closePanel = useCallback(() => {
    setIsPanelOpen(false)
    editorRef.current?.focus()
  }, [editorRef])

  // Toggle the panel open/closed
  const togglePanel = useCallback(() => {
    if (!shouldShowCopilot || isGenerating || !canToggle) return

    if (isPanelOpen) {
      closePanel()
    } else {
      setIsPanelOpen(true)
    }
  }, [shouldShowCopilot, isGenerating, canToggle, isPanelOpen, closePanel])

  // Handler for when content is generated - updates body and optionally title
  const handleGenerate = useCallback(
    (generatedTitle: string, generatedDescription: string) => {
      onChange(generatedDescription, generateTitle ? generatedTitle : undefined)
    },
    [onChange, generateTitle],
  )

  // Close the panel when input is focused if anchored to top (no existing body)
  const handleInputFocus = useCallback(() => {
    if (isPanelOpen && !body.trim()) {
      setIsPanelOpen(false)
    }
  }, [isPanelOpen, body])

  // Commands object for ScopedCommands
  const commands = useMemo(
    () => ({
      [commandId]: togglePanel,
    }),
    [commandId, togglePanel],
  )

  // Compute anchor position based on body content and generating state
  const anchorPosition = body.trim() || isGenerating ? 'bottom' : 'top'

  // Container style with CSS variables for bottom padding and editor sizing
  const containerStyle: React.CSSProperties | undefined = useMemo(() => {
    if (isPanelOpen) {
      return {
        '--markdown-input-bottom-padding': body.trim() || isGenerating ? `${panelHeight}px` : undefined,
        '--markdown-editor-min-height': `${panelHeight + 100}px`,
      } as React.CSSProperties
    }
    return undefined
  }, [isPanelOpen, body, isGenerating, panelHeight])

  // Props to spread on CopilotGenerateDescriptionPanel
  const panelProps: Omit<CopilotGenerateDescriptionPanelProps, 'className'> = useMemo(
    () => ({
      isOpen: isPanelOpen,
      onClose: closePanel,
      onGenerate: handleGenerate,
      repoOwner,
      repoName,
      title,
      body,
      onPendingChange: setIsGenerating,
      anchorPosition,
      onHeightChange: setPanelHeight,
      editorRef,
      generateTitle,
    }),
    [
      isPanelOpen,
      closePanel,
      handleGenerate,
      repoOwner,
      repoName,
      title,
      body,
      anchorPosition,
      editorRef,
      generateTitle,
    ],
  )

  return {
    isPanelOpen,
    isGenerating,
    panelHeight,
    shouldShowCopilot,
    togglePanel,
    closePanel,
    handleGenerate,
    handleInputFocus,
    commands,
    panelProps,
    containerStyle,
    setIsGenerating,
    setPanelHeight,
  }
}
